import {
  MutationFunction,
  MutationOptions,
  QueryClient,
  UseMutationResult,
  useMutation,
  useQueryClient
} from '@tanstack/react-query'

import { APIRequest } from 'models/API'
import { APIRequestErrorType, APIRequestState } from 'constants/API'
import { AxiosError } from 'axios'
import { logger } from 'utils/logging'

/**
 * Helper wrapper function for easy generating of basic mutations.   
 * This wrapper automatically injects queryClient and provides param for invalidating function triggered on mutation success.  
 * This is an early development version and things might change, keep that in mind.
 * 
 * @template DataType - The expected data type returned by the mutationFn
 * @template VarType - Variables passed to the mutation function
 * @template ErrorType - Error type (defaults to AxiosError)
 *
 * @param mutationFnc - The mutation function that returns DataType
 * @param invalidationFnc - Optional callback only for query invalidation
 * @param options - Optional MutationOptions - for your UI/store side-effects and other success handling.
 * 
 * @returns mutation result
 */
const DEFAULT_RETRY_COUNT = 3

/**
 * React Query `retry` callback that follows the default 3-retry policy
 * but skips retrying when the response is a 404. Use for endpoints where
 * 404 is an expected "not configured" / "no resource" signal.
 */
export const retryUnlessNotFound = (failureCount: number, error: unknown): boolean => {
  if (error instanceof AxiosError && error.response?.status === 404) {
    if (failureCount === 0) {
      logger.info('Query short-circuited on 404', undefined, {
        context: {
          component: 'retryUnlessNotFound',
          metadata: { url: error.config?.url, method: error.config?.method },
        },
      })
    }
    return false
  }
  return failureCount < DEFAULT_RETRY_COUNT
}

export function getMutation<
  DataType,
  VarType,
  ErrorType = AxiosError
>(
  mutationFnc: MutationFunction<DataType, VarType>,
  invalidationFnc?: (client: QueryClient, variables: VarType, data: any) => void,
  // For now just like this for basic customization
  options?: MutationOptions<DataType, ErrorType, VarType>
) {
  return (() => {
    const client = useQueryClient()

    return useMutation<DataType, ErrorType, VarType>({
      mutationFn: mutationFnc,
      ...options,
      onSuccess: (data, variables, context) => {
        invalidationFnc?.(client, variables, data)
        options?.onSuccess?.(data, variables, context)
      },
    })
  })()
}

/**
 * Converts a React Query UseMutationResult to an APIRequest-compatible shape.
 * Useful for bridging React Query mutations with legacy components
 * that expect APIRequest (e.g. AutoFireActionInput, RequestStatus).
 */
export function mutationToAPIRequest<TData = unknown, TError = AxiosError>(
  mutation: UseMutationResult<TData, TError, any, any>
): APIRequest<TData> {
  if (mutation.isPending) return new APIRequest(APIRequestState.RUNNING)
  if (mutation.isSuccess) return APIRequest.factoryOK(mutation.data)
  if (mutation.isError) return APIRequest.factoryError(APIRequestErrorType.UNKNOWN_ERROR, mutation.error)
  return new APIRequest()
}

/**
 * Converts a React Query UseQueryResult to an APIRequest-compatible shape.
 * Useful for bridging React Query queries with legacy components
 * that expect APIRequest (e.g. FetchedContent, RequestStatus).
 */
export function queryToAPIRequest<TData = unknown>(
  query: { isPending: boolean, isSuccess: boolean, isError: boolean, data?: TData, error: any, isFetching: boolean },
  options?: {
    treatDataAsPending?: (data?: TData) => boolean
  }
): APIRequest<TData | undefined> {
  if (query.isPending || options?.treatDataAsPending?.(query.data)) return new APIRequest(APIRequestState.RUNNING)
  if (query.isSuccess) return APIRequest.factoryOK(query.data)
  if (query.isError) return APIRequest.factoryError(APIRequestErrorType.UNKNOWN_ERROR, query.error)
  return new APIRequest()
}
